/**
 * @file FooterColumnsScheme.tsx
 * @description Схема «Колонки на сайте (ПК)» во вкладке «Подвал» (phase19.16,
 * приёмка Р2а; SB113, SB114; uiux-footer-columns §4.4, §4.5, §5): ячейки по
 * фактическим данным — «О магазине» (части), колонки меню, которые увидит
 * покупатель (заголовок или «без заголовка», «авто, N кат.» / «N пунктов»),
 * пунктир «+ ещё одна», пока есть место; счётчик «N из 3», пометка о пустых
 * колонках и ссылка «Настроить колонки меню →».
 *
 * Нажатие на колонку меню открывает «Меню → Подвал» с этой колонкой
 * (`&column=<id>`), на «О магазине» — прокрутка к её флажкам. Подсветки
 * колонки на канве при наведении нет: мост канвы умеет подсвечивать блоки,
 * но не области подвала — TD-359 (docs/TECH-DEBT.md).
 *
 * Сетка — по ширине своего блока (как SurfaceFillField): 3 в ряд в панели
 * 380 и на телефоне, 4 — от 440 px блока; подписи переносятся, не обрезаются.
 */

import type { FooterLayout } from "@chaiberi/shared";
import { Lock, Plus } from "lucide-react";
import { Link } from "react-router";

import { Button } from "../../ui/button";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { Skeleton } from "../../ui/skeleton";
import { aboutSummary, columnSummary, columnTitle, plural } from "../../../lib/site-view/footer-columns";
import type { SiteMenuItem } from "../../../lib/site-view/types";
import { cn } from "@/lib/utils";

interface Props {
  layout: FooterLayout<SiteMenuItem> | null;
  /** Ошибка загрузки меню; при ней и без данных — полоса «Не удалось…». */
  error: string | null;
  onRetry: () => void;
  /** Известно ли число категорий автосписка. */
  categoriesKnown: boolean;
  /** Адрес «Меню → Подвал». */
  menuHref: string;
  /** Копирайт выбран в колонку «О магазине» и включён; при пустой колонке сайт ставит его сверху полосы. */
  copyrightInAbout?: boolean;
  /** «О магазине» — прокрутка к её флажкам. */
  onAbout: () => void;
}

const GRID = "grid grid-cols-3 gap-1 @min-[440px]:grid-cols-4";
const CELL = "flex h-full min-h-14 w-full min-w-0 flex-col items-start gap-0.5 break-words rounded-lg p-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring";
const columnHref = (menuHref: string, id: string | null) => (id ? `${menuHref}&column=${encodeURIComponent(id)}` : menuHref);

export function FooterColumnsScheme({ layout, error, onRetry, categoriesKnown, menuHref, copyrightInAbout = false, onAbout }: Props) {
  if (!layout && error) {
    return (
      <NoticeStrip tone="error" action={<Button variant="outline" onClick={onRetry}>Повторить</Button>}>
        Не удалось загрузить меню подвала — число колонок неизвестно. На сайте остаётся опубликованный подвал.
      </NoticeStrip>
    );
  }
  if (!layout) {
    return (
      <div className="@container flex min-w-0 flex-col gap-2" aria-busy="true">
        <div className={GRID}>
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-14 rounded-lg" />)}
        </div>
        <span className="type-caption text-on-surface-variant">Считаем колонки…</span>
      </div>
    );
  }

  const shown = layout.menuShown ? layout.columns.filter((c) => !c.hidden && !c.empty) : [];
  const total = layout.columns.length;
  const firstEmpty = layout.columns.find((c) => c.empty);
  // Копирайт живёт в «О магазине», только если колонка есть; иначе сайт ставит его сверху полосы.
  const hasAbout = layout.about.parts.length > 0;
  const about = hasAbout ? [aboutSummary(layout.about.parts), copyrightInAbout ? "копирайт" : null].filter(Boolean).join(", ") : null;
  return (
    <div className="@container flex min-w-0 flex-col gap-2">
      <ul className={GRID} aria-label="Колонки подвала на ПК">
        <li className="min-w-0">
          <button
            type="button"
            onClick={onAbout}
            className={cn(CELL, about ? "bg-surface-low" : "border border-dashed border-on-surface/30")}
          >
            {/* Узкая ячейка (панель 320 в карточке группы): замок переносится, а не слово «магазин|е». */}
            <span className="type-caption flex w-full flex-wrap items-start justify-between gap-1 font-medium text-on-surface">
              <span className="break-words">О магазине</span>
              <Lock aria-label="закреплена" className="mt-0.5 size-3 shrink-0" />
            </span>
            <span className="type-caption text-on-surface-variant">{about ?? "пусто"}</span>
          </button>
        </li>
        {shown.map((column) => (
          <li key={column.id ?? "loose"} className="min-w-0">
            <Link
              to={columnHref(menuHref, column.id)}
              title={column.title ?? undefined}
              className={cn(CELL, "bg-surface-low hover:bg-primary/8")}
            >
              <span className={cn("type-caption w-full break-words font-medium", column.title ? "text-on-surface" : "text-on-surface-variant")}>
                {columnTitle(column)}
              </span>
              <span className="type-caption text-on-surface-variant">{columnSummary(column, categoriesKnown)}</span>
            </Link>
          </li>
        ))}
        {layout.menuShown && total < layout.limit ? (
          <li className="min-w-0">
            <Link to={menuHref} className={cn(CELL, "items-center justify-center border border-dashed border-on-surface/30 text-on-surface-variant")}>
              <Plus aria-hidden className="size-4" />
              <span className="type-caption">ещё одна</span>
            </Link>
          </li>
        ) : null}
      </ul>
      {!about ? (
        <p className="type-caption text-on-surface-variant">О магазине — пусто, на сайте её не будет. Включите логотип или контакты.</p>
      ) : null}
      <p className="type-caption text-on-surface-variant">На телефоне колонки идут стопкой, с заголовком — сворачиваются.</p>
      {!layout.menuShown ? (
        <p className="type-body text-on-surface-variant">Меню подвала выключено — колонок меню на сайте нет.</p>
      ) : total === 0 ? (
        <p className="type-body text-on-surface-variant">Колонок меню пока нет — в подвале будут логотип, контакты и сведения о продавце.</p>
      ) : (
        <p className="type-body text-on-surface tabular-nums">
          Колонок меню: {layout.visibleColumns} из {layout.limit} на сайте
          {total >= layout.limit && !layout.overLimit ? " — больше не поместится" : ""}
        </p>
      )}
      {layout.overLimit ? (
        <NoticeStrip tone="error">
          Колонок меню {total}, а помещается {layout.limit} — сайт не опубликуется. Объедините колонки или удалите лишнюю.
        </NoticeStrip>
      ) : null}
      {firstEmpty ? (
        <NoticeStrip tone="warning" action={<Link to={columnHref(menuHref, firstEmpty.id)} className="type-body inline-flex min-h-12 items-center font-medium text-primary hover:underline">Показать →</Link>}>
          Ещё {layout.emptyColumns} {plural(layout.emptyColumns, "пустая колонка не видна", "пустые колонки не видны", "пустых колонок не видны")} на сайте.
        </NoticeStrip>
      ) : null}
      <Button asChild variant="outline" className="self-start">
        <Link to={menuHref}>{total === 0 ? "Добавить колонку →" : "Настроить колонки меню →"}</Link>
      </Button>
    </div>
  );
}
